(() => {
  'use strict';
  const KEYS = Object.freeze({ id: 'LOCK_RRPP_ID', source: 'LOCK_RRPP_SOURCE', version: 'LOCK_RRPP_VERSION' });
  const normalize = value => typeof value === 'string' ? value.trim().toUpperCase() : '';
  const registry = config => {
    if (config?.version !== 1 || config.policy !== 'last_valid_link_manual_priority' || !Array.isArray(config.members)) throw new Error('Invalid RRPP configuration');
    const result = new Map(), seen = new Set();
    for (const member of config.members) {
      const code = normalize(member.code);
      if (!/^[A-Z0-9][A-Z0-9_-]{1,31}$/.test(code) || typeof member.name !== 'string' || !member.name.trim() || seen.has(code)) throw new Error('Invalid RRPP member');
      seen.add(code);
      if (member.active === true) result.set(code, { code, name: member.name.trim() });
    }
    return result;
  };
  const candidate = (value, members) => members.get(normalize(value)) || null;
  const fromURL = (url, members) => {
    try { const values = new URL(url).searchParams.getAll('rrpp'); return values.length === 1 ? candidate(values[0], members) : null; } catch { return null; }
  };
  const fromCart = (cart, members) => {
    const attrs = cart?.attributes || {};
    const member = candidate(attrs[KEYS.id], members);
    return member && ['manual', 'link'].includes(attrs[KEYS.source]) && attrs[KEYS.version] === '1' ? { ...member, source: attrs[KEYS.source] } : null;
  };
  const select = (saved, linked, consent) => saved?.source === 'manual' ? saved : linked && consent ? { ...linked, source: 'link' } : saved;
  const attributes = choice => ({ [KEYS.id]: choice?.code || '', [KEYS.source]: choice?.source || '', [KEYS.version]: choice ? '1' : '' });
  const api = Object.freeze({ KEYS, normalize, registry, candidate, fromURL, fromCart, select, attributes });
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document === 'undefined') return;
  const configNode = document.getElementById('LockRRPPConfig');
  if (!configNode) return;
  let config, members;
  try { config = JSON.parse(configNode.textContent); members = registry(config); } catch { return; }
  if (config.enabled !== true || members.size === 0) return;
  const base = window.Shopify?.routes?.root || '/';
  const linked = fromURL(window.location.href, members);
  let saved = null, ready = false, busy = false, suppressed = false, message = '', error = false, panels = [];
  const consent = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; } catch { return false; }
  };
  const draw = () => {
    for (const panel of panels) {
      panel.hidden = false;
      if (!panel.dataset.rrppOpened && !suppressed && (saved || linked)) { panel.open = true; panel.dataset.rrppOpened = 'true'; }
      const input = panel.querySelector('[data-rrpp-code]');
      if (!error && document.activeElement !== input) input.value = saved?.code || (!suppressed && linked?.code) || '';
      panel.querySelectorAll('button').forEach(button => { button.disabled = busy || !ready; });
      const status = panel.querySelector('[data-rrpp-status]');
      status.textContent = message ? panel.dataset[message] || message : saved ? `${panel.dataset.selected} ${saved.name} (${saved.code})` : !suppressed && linked && panel.dataset.linked ? panel.dataset.linked.replace('%NAME%', linked.name) : panel.dataset.direct;
      status.dataset.state = error ? 'error' : 'normal';
      panel.setAttribute('aria-busy', String(busy || !ready));
    }
  };
  const setMessage = (key, failed = false) => { message = key; error = failed; draw(); };
  const request = async (path, options) => {
    const response = await fetch(base + path, { credentials: 'same-origin', ...options });
    if (!response.ok) throw new Error('RRPP cart request failed');
    return response.json();
  };
  const write = async (choice, success = '') => {
    if (busy || !ready) return false;
    busy = true; setMessage('pending');
    try {
      const cart = await request('cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes: attributes(choice) }) });
      const returned = fromCart(cart, members);
      if (choice ? returned?.code !== choice.code || returned?.source !== choice.source : Boolean(cart?.attributes?.[KEYS.id])) throw new Error('RRPP cart verification failed');
      saved = returned; suppressed = !choice; message = success; error = false;
      return true;
    } catch { message = 'error'; error = true; return false; } finally { busy = false; draw(); }
  };
  const apply = async panel => {
    const input = panel.querySelector('[data-rrpp-code]');
    const member = candidate(input.value, members);
    if (!member) { input.setAttribute('aria-invalid', 'true'); setMessage('invalid', true); return; }
    input.removeAttribute('aria-invalid');
    await write({ ...member, source: 'manual' });
  };
  const mount = () => {
    panels = [...document.querySelectorAll('[data-lock-rrpp-panel]')];
    for (const panel of panels) {
      if (panel.dataset.rrppReady) continue;
      panel.dataset.rrppReady = 'true';
      panel.querySelector('[data-rrpp-apply]').addEventListener('click', () => apply(panel));
      panel.querySelector('[data-rrpp-clear]').addEventListener('click', async () => {
        panel.querySelector('[data-rrpp-code]').removeAttribute('aria-invalid');
        if (await write(null, 'cleared')) panels.forEach(item => { item.querySelector('[data-rrpp-code]').value = ''; });
      });
      panel.querySelector('[data-rrpp-code]').addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); apply(panel); }
      });
    }
    draw();
  };
  const automatic = async () => {
    if (!ready || busy || suppressed) return;
    const choice = select(saved, linked, true); // Enlace de RRPP: atribución automática siempre (decisión del propietario 15/10/2026); el código manual sigue teniendo prioridad.
    if (choice && (choice.code !== saved?.code || choice.source !== saved?.source)) await write(choice);
  };
  mount();
  setMessage('pending');
  request('cart.js').then(async cart => {
    saved = fromCart(cart, members); ready = true; message = ''; draw(); await automatic();
  }).catch(() => { ready = true; setMessage('error', true); });
  document.addEventListener('visitorConsentCollected', automatic);
  document.addEventListener('click', event => {
    if (!busy || !event.target.closest?.('[name="checkout"],a[href^="/checkout"],.additional-checkout-buttons')) return;
    event.preventDefault(); event.stopImmediatePropagation(); setMessage('pending');
  }, true);
  document.addEventListener('submit', event => {
    if (!busy || !event.submitter?.matches?.('[name="checkout"]')) return;
    event.preventDefault(); event.stopImmediatePropagation(); setMessage('pending');
  }, true);
  document.addEventListener('shopify:section:load', mount);
  let frame = 0;
  new MutationObserver(records => {
    if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-lock-rrpp-panel]') || node.querySelector?.('[data-lock-rrpp-panel]'))))) return;
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; mount(); });
  }).observe(document.body, { childList: true, subtree: true });
})();
